// Dual movement tracker: maintains separate MAIN vs BED (bed legs) state for systems with bed motors.
// Returned unified state shape:
// {
//   main: {
//     current: 'couch'|'closet'|'bed'|'manual'|'???',
//     moving: 'up'|'down'|null,
//     counts: { couch:0, closet:0, bed:0, manual:0 }, // transition counts (increment previous preset when NEW preset appears)
//     lastChangeAt: ISOString|null,
//     lastEventRaw: { key,value,timestamp }|null
//   },
//   bed: {
//     current: 'out'|'in'|'middle'|'???',  // static position when stopped
//     moving: 'up'|'down'|null,                // direction while p/bed/moving active
//     lastChangeAt: ISOString|null,
//     lastEventRaw: { key,value,timestamp }|null
//   }|null,
//   hasBed: boolean
// }
// Cloud Bed notes: main up motion begins with p/system_state 20, down motion with 17, completes via p/auto/stop closet|couch.
// Pocket Studio: main can reach 'bed' preset, then bed motors (p/bed/*) run to reach out/in/middle.
// Offline events (k/status offline) ignored.

const unknownString = 'unknown';
const MOVEMENT_STALE_MS = 60 * 1000;

function toTimestampMs(ts) {
    if (!ts) return null;
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? null : parsed;
}

function entryTimestampMs(entry) {
    if (!entry) return null;
    return toTimestampMs(entry?.lastEventRaw?.timestamp) ?? toTimestampMs(entry?.lastChangeAt);
}

function clearStaleMovement(state, now = Date.now()) {
    if (!state) return state;
    let main = state.main;
    let bed = state.bed;
    let changed = false;

    if (main?.moving) {
        const ts = entryTimestampMs(main);
        if (ts !== null && now - ts > MOVEMENT_STALE_MS) {
            main = { ...main, moving: null };
            changed = true;
        }
    }

    if (bed?.moving) {
        const ts = entryTimestampMs(bed);
        if (ts !== null && now - ts > MOVEMENT_STALE_MS) {
            bed = { ...bed, moving: null };
            changed = true;
        }
    }

    return changed ? { ...state, main, bed } : state;
}

function emptyMain() {
    return {
        current: unknownString,
        moving: null,
        counts: { couch: 0, closet: 0, bed: 0, manual: 0 },
        lastChangeAt: null,
        lastEventRaw: null
    };
}

function emptyBed() {
    return {
        current: unknownString,
        moving: null,
        lastChangeAt: null,
        lastEventRaw: null
    };
}

function emptyState(hasBed = false) {
    return {
        main: emptyMain(),
        bed: hasBed ? emptyBed() : null,
        hasBed
    };
}

function mapAutoButton(val) {
    if (!val) return null;
    if (val === 'bed') return 'bed';
    if (val === 'closet') return 'closet';
    if (val === 'couch') return 'couch';
    return null;
}

// Map bed stop value to bed current position (treat legacy closet as 'in').
function mapBedStopValue(val) {
    if (!val) return null;
    if (val === 'closet') return 'in';
    if (['out', 'middle', 'in'].includes(val)) return val;
    return null;
}

function mapMotorStop(val) {
    if (val === 'closet') return 'closet';
    if (val === 'couch') return 'couch';
    if (val === 'bed') return 'bed';
    return null;
}

function isManualSystemState(val) {
    return val === 8 || val === 9;
}

function isDirectionalSystemState(val) {
    return val === 10 || val === 11 || val === 16 || val === 17 || val === 20 || val === 21;
}

function isDirectionalSystemStateLeft(val) {
    return val === 10 || val === 16 || val === 20;
}

function isDirectionalSystemStateRight(val) {
    return val === 11 || val === 17 || val === 21;
}

function applyMainTransition(prevMain, nextPreset, eventTuple, movingDir = null) {
    if (!nextPreset && movingDir === null) return prevMain; // nothing to change
    const counts = { ...prevMain.counts };
    // Increment count for the NEW position we're transitioning TO (not FROM)
    if (nextPreset && prevMain.current !== nextPreset) {
        if (counts[nextPreset] !== undefined) counts[nextPreset] += 1;
    }
    return {
        current: nextPreset ? nextPreset : prevMain.current,
        moving: movingDir,
        counts,
        lastChangeAt: eventTuple[2] || new Date().toISOString(),
        lastEventRaw: { key: eventTuple[0], value: eventTuple[1], timestamp: eventTuple[2] }
    };
}

function applyBedTransition(prevBed, bedUpdate, eventTuple) {
    if (!prevBed) return prevBed; // system has no bed
    const next = { ...prevBed };
    if (bedUpdate.moving !== undefined) next.moving = bedUpdate.moving;
    if (bedUpdate.current !== undefined) next.current = bedUpdate.current;
    next.lastChangeAt = eventTuple[2] || new Date().toISOString();
    next.lastEventRaw = { key: eventTuple[0], value: eventTuple[1], timestamp: eventTuple[2] };
    return next;
}

export function applyEventsToMovement(prev, events, systemTypeLabel) {
    const hasBed = ['Cloud Bed', 'Cloud Bed Table', 'Pocket Studio'].includes(systemTypeLabel);
    const isCloudBed = ['Cloud Bed', 'Cloud Bed Table'].includes(systemTypeLabel);
    let state = prev ? {
        main: { ...prev.main, counts: { ...prev.main.counts } },
        bed: prev.bed ? { ...prev.bed } : null,
        hasBed: prev.hasBed
    } : emptyState(hasBed);
    state.hasBed = hasBed;
    if (hasBed && !state.bed) state.bed = emptyBed();
    if (!hasBed) state.bed = null;
    if (!Array.isArray(events) || events.length === 0) return clearStaleMovement(state);
    for (const tuple of events) {
        if (!Array.isArray(tuple) || tuple.length < 3) continue;
        const [key, value] = tuple;
        let mainPreset = null; let mainMoving = null; let bedUpdate = null;
        // movement event tuple processed
        //console.log(`Processing movement event: ${JSON.stringify(tuple)}`);
        switch (key) {
            //case 'p/auto/button':
            //case 'r/auto/button':
            case 'p/auto/stop':
                mainPreset = mapAutoButton(value);
                mainMoving = '';
                break;
            case 'p/motor/stop':
            case 'p/motor/auto/stop':
                mainPreset = mapMotorStop(value); //i think this is supposed to be an int instead of a string compare
                mainMoving = '';
                break;
            case 'p/system_state':
                if (isManualSystemState(value)) mainPreset = 'manual';
                if (isDirectionalSystemStateLeft(value)) mainMoving = (isCloudBed ? 'up' : 'left');
                if (isDirectionalSystemStateRight(value)) mainMoving = (isCloudBed ? 'down' : 'right');
                break;
            case 'p/bed/moving':
            case 'r/bed/moving':
                if (state.hasBed) {
                    bedUpdate = { moving: value };
                    //if (state.main.current !== 'bed') mainPreset = 'bed';
                }
                break;
            case 'p/bed/stop':
                if (state.hasBed) {
                    bedUpdate = { current: mapBedStopValue(value), moving: null };
                    //if (state.main.current !== 'bed') mainPreset = 'bed';
                }
                break;
            case 'k/status':
                if (value === 'offline') {
                    // ignore offline for movement state
                }
                break;
            default: break;
        }
        if (mainPreset || mainMoving !== null) {
            state.main = applyMainTransition(state.main, mainPreset, tuple, mainMoving !== null ? mainMoving : state.main.moving);
        }
        if (bedUpdate) {
            state.bed = applyBedTransition(state.bed, bedUpdate, tuple);
        }
    }
    return clearStaleMovement(state);
}

export function movementStateFromAggregated(aggregatedData, movementEvents, systemTypeLabel) {
    const hasBed = ['Cloud Bed', 'Cloud Bed Table', 'Pocket Studio'].includes(systemTypeLabel);
    const isCloudBed = ['Cloud Bed', 'Cloud Bed Table'].includes(systemTypeLabel);
    const state = emptyState(hasBed);
    if (!aggregatedData) return state;
    // Collect all candidate main events with timestamps
    const mainKeys = ['p/auto/stop', 'p/motor/auto/stop', 'p/motor/stop', 'p/system_state'];
    const bedKeys = hasBed ? ['p/bed/stop', 'p/bed/moving', 'r/bed/moving'] : [];
    const mainEvents = [];
    const bedEvents = [];
    [...mainKeys, ...bedKeys].forEach(k => {
        if (k in aggregatedData) {
            const ts = movementEvents?.[k] || new Date().toISOString();
            const evt = { key: k, value: aggregatedData[k], ts };
            if (mainKeys.includes(k)) mainEvents.push(evt); else bedEvents.push(evt);
        }
    });
    // Determine definitive preset from latest stop-type main event.
    const STOP_KEYS = ['p/auto/stop', 'p/motor/auto/stop', 'p/motor/stop'];
    let latestStop = null;
    mainEvents.forEach(e => { if (STOP_KEYS.includes(e.key)) { if (!latestStop || e.ts > latestStop.ts) latestStop = e; } });
    // Determine directional system_state (if no stop superseding it)
    let latestDirectional = null;
    mainEvents.forEach(e => { if (e.key === 'p/system_state' && isDirectionalSystemState(e.value)) { if (!latestDirectional || e.ts > latestDirectional.ts) latestDirectional = e; } });
    // Manual system_state (8/9)
    let latestManual = null;
    mainEvents.forEach(e => { if (e.key === 'p/system_state' && isManualSystemState(e.value)) { if (!latestManual || e.ts > latestManual.ts) latestManual = e; } });

    // Decide preset
    if (latestStop) {
        let preset = null;
        if (latestStop.key === 'p/auto/stop') preset = mapAutoButton(latestStop.value);
        else preset = mapMotorStop(latestStop.value);
        if (preset) {
            state.main.current = preset;
            state.main.moving = null; // stop finalizes movement
            state.main.lastChangeAt = latestStop.ts;
            state.main.lastEventRaw = { key: latestStop.key, value: latestStop.value, timestamp: latestStop.ts };
        }
    } else if (latestManual) {
        state.main.current = 'manual';
        state.main.moving = null;
        state.main.lastChangeAt = latestManual.ts;
        state.main.lastEventRaw = { key: latestManual.key, value: latestManual.value, timestamp: latestManual.ts };
    } else if (latestDirectional) {
        // Moving between presets, but no final stop yet present in snapshot
        state.main.current = 'bed'; // treat active vertical movement as bed context
        state.main.moving = isDirectionalSystemStateLeft(latestDirectional.value) ? (isCloudBed ? 'up' : 'left') : isDirectionalSystemStateRight(latestDirectional.value) ? (isCloudBed ? 'down' : 'right') : null;
        state.main.lastChangeAt = latestDirectional.ts;
        state.main.lastEventRaw = { key: latestDirectional.key, value: latestDirectional.value, timestamp: latestDirectional.ts };
    }

    // Bed events
    if (bedEvents.length > 0 && hasBed) {
        // Prefer latest stop for bed position, else latest moving for direction
        let latestBedStop = null; let latestBedMoving = null;
        bedEvents.forEach(e => {
            if (e.key === 'p/bed/stop') { if (!latestBedStop || e.ts > latestBedStop.ts) latestBedStop = e; }
            if (e.key === 'p/bed/moving' || e.key === 'r/bed/moving') { if (!latestBedMoving || e.ts > latestBedMoving.ts) latestBedMoving = e; }
        });
        if (latestBedStop) {
            state.bed.current = mapBedStopValue(latestBedStop.value) || state.bed.current;
            state.bed.moving = null;
            state.bed.lastChangeAt = latestBedStop.ts;
            state.bed.lastEventRaw = { key: latestBedStop.key, value: latestBedStop.value, timestamp: latestBedStop.ts };
        } else if (latestBedMoving) {
            state.bed.moving = latestBedMoving.value;
            state.bed.lastChangeAt = latestBedMoving.ts;
            state.bed.lastEventRaw = { key: latestBedMoving.key, value: latestBedMoving.value, timestamp: latestBedMoving.ts };
        }
    }
    return clearStaleMovement(state);
}

export function emptyMovementState(hasBed = false) { return emptyState(hasBed); }
